/** One icon API for shared screens: the web draws the Material path, and the native app swaps in the matching SF Symbol
 *  (src/shared/ui/iconSymbols.ts) by registering a system renderer at start-up, so src/ never imports native views. */
import React from 'react';
import Svg, { Path } from 'react-native-svg';

export type IconWeight = 'ultraLight' | 'thin' | 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'heavy' | 'black';
export type IconRenderingMode = 'monochrome' | 'hierarchical' | 'palette' | 'multicolor';
/** System colours resolve natively, so they follow the user's accent colour, appearance and Increase Contrast. */
export type IconSystemTint = 'accent' | 'label' | 'secondaryLabel' | 'tertiaryLabel';

export type IconProps = {
  d: string;
  size?: number;
  color: string;
  viewBox?: string;
  /** Overrides the symbol the path maps to. */
  symbol?: string;
  /** Hover and selected states: the symbol's fill variant natively, `filledD` on the web. */
  filled?: boolean;
  filledD?: string;
  weight?: IconWeight;
  scale?: 'small' | 'medium' | 'large';
  renderingMode?: IconRenderingMode;
  secondaryColor?: string;
  tertiaryColor?: string;
  tint?: IconSystemTint;
};

/** The Material glyph itself; system renderers fall back to it for paths with no symbol. */
export function PathIcon({ d, filled, filledD, size = 24, color, viewBox = '0 -960 960 960' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox={viewBox}>
      <Path d={filled && filledD ? filledD : d} fill={color} />
    </Svg>
  );
}

let SystemIcon: React.ComponentType<IconProps> | null = null;

export function registerSystemIcon(renderer: React.ComponentType<IconProps> | null) {
  SystemIcon = renderer;
}

export function Icon(props: IconProps) {
  const Renderer = SystemIcon ?? PathIcon;
  return <Renderer {...props} />;
}
